import * as React from "react";
import { Dispatch } from "redux";
import queryString from "query-string-for-all";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import Helmet from "react-helmet";
import { show } from "redux-modal";
import { loadMonitoring } from "../../../redux/modules/monitoring/actionCreators";
import { history } from "../../../browserHistory";
import {
  updateIsMultiSiteRoute,
  selectActiveSite,
} from "../../../redux/modules/_BETA/selections";
import Equip from "../../../components/Equip";
import Loading from "../../../components/Loading";
import { Wrap } from "../../../components/Layout/Page";
import { getPersisted, setPersisted } from "../../../utils/storage";
import { equipColumns } from "../../../utils/columns";
import * as routes from "../../../utils/routes";

const DEFAULT_SORT = {
  option: equipColumns.TRIGGERED_RULES.key,
  direction: equipColumns.TRIGGERED_RULES.sortDirection,
};

const PERSIST_KEY = "equipGrid";
const PERSIST_PROP = "Equip-colSort";

interface ConnectedProps {
  appSetupComplete: Insite.AppState["appSetup"]["appSetupComplete"];
  siteGroups: any;
  loading: boolean;
  ready: any;
  shouldRequest: Insite.AppState["requestSchedule"]["shouldRequestMonitoring"];
  error: Insite.AppState["insights"]["error"];
  selectedSites: Insite.AppState["betaSelections"]["selectedSites"];
  activeSite: Insite.AppState["betaSelections"]["activeSite"];
  showSnoozed: Insite.AppState["insights"]["showSnoozed"];
  urlHistory: Insite.AppState["urlHistory"];
  timeRange: Insite.AppState["betaSelections"]["daysRange"];
  userAccess: Insite.AppState["auth"]["user"]["access"];
}

interface DispatchProps {
  setMultiSite: (syncoId: string) => void;
  requestInsights: () => void;
  showModal: (
    equipRuleIds: string[],
    hits: any,
    createNewIssue: boolean
  ) => void;
}

interface StateProps {
  redirectingToMonitoring: boolean;
  sort: {
    option: string;
    direction: string;
  };
}

interface MatchParams {
  syncoId: string;
  ruleId: string;
  insightType: routes.InsightType;
}

type Props = RouteComponentProps<MatchParams> & ConnectedProps & DispatchProps;

const mapStateToProps = (state: Insite.AppState) => {
  return {
    appSetupComplete: state.appSetup.appSetupComplete,
    siteGroups: state.monitoring.siteGroups,
    loading: state.monitoring.loading,
    ready: state.monitoring.ready,
    shouldRequest: state.requestSchedule.shouldRequestMonitoring,
    error: state.insights.error,
    selectedSites: state.betaSelections.selectedSites,
    activeSite: state.betaSelections.activeSite,
    showSnoozed: state.insights.showSnoozed,
    urlHistory: state.urlHistory,
    timeRange: state.betaSelections.daysRange,
    userAccess: state.auth.user.access,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    requestInsights: () => {
      dispatch(loadMonitoring());
    },
    showModal: (equipRuleIds: string[], hits: any, createNewIssue = false) => {
      // Also include equipNames by searching through all hits to find ones that
      // match the supplied ids
      const equipNames = equipRuleIds
        .map((id: string) => {
          const hit = hits.find((h: any) => h.equipRuleId === id);
          return hit ? hit.targetName : null;
        })
        .filter((h) => h !== null);
      return () =>
        dispatch(
          show("TRIAGE_ACTION_MODAL", {
            equipRuleIds,
            equipNames,
            createNewIssue,
          })
        );
    },
    setMultiSite: (syncoId: string) => {
      dispatch(updateIsMultiSiteRoute(false));
      dispatch(selectActiveSite(syncoId));
    },
  };
};

class EquipScene extends React.Component<Props, StateProps> {
  constructor(props: Props) {
    super(props);
    const sort = getPersisted(
      { key: PERSIST_KEY, prop: PERSIST_PROP },
      DEFAULT_SORT
    );
    this.state = {
      redirectingToMonitoring: false,
      sort,
    };
  }

  componentDidMount() {
    const { appSetupComplete, shouldRequest } = this.props;
    const { syncoId } = this.props.match.params;
    this.props.setMultiSite(syncoId);

    if (appSetupComplete && shouldRequest) {
      this.props.requestInsights();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.props.appSetupComplete) return;
    const pastQS = queryString.parse(prevProps.location.search);
    const currentQS = queryString.parse(this.props.location.search);

    /**
     * If selected sites has changed; then redirect to '/monitoring'
     */
    if (pastQS.sites !== currentQS.sites) {
      this.redirectToMonitoring();
    } else if (
      !this.state.redirectingToMonitoring &&
      this.props.shouldRequest &&
      !this.props.loading
    ) {
      this.props.requestInsights();
    }
  }

  handleSort = (option: string) => () => {
    this.setState((state) => {
      const direction = state.sort.direction === "asc" ? "desc" : "asc";

      setPersisted(
        { key: PERSIST_KEY, prop: PERSIST_PROP },
        { option, direction }
      );
      return {
        sort: {
          option,
          direction,
        },
      };
    });
  };

  redirectToMonitoring = () => {
    this.setState({
      redirectingToMonitoring: true,
    });

    const { search } = history.location;
    history.push({
      pathname: "/analysis/monitoring",
      search,
    });
  };

  render() {
    const { syncoId, ruleId } = this.props.match.params;
    const { error, siteGroups, showSnoozed, appSetupComplete, userAccess } = this.props;
    const { sort } = this.state;

    // if (!appSetupComplete || this.props.loading || !this.props.ready) return <Loading />
    if (!appSetupComplete) return <Loading />;
    if (!this.props.ready) {
      return <Loading />;
    }

    if (this.props.loading && !this.props.ready) return <Loading />;

    // TODO: Error state when requesting data for site that doesn't exist/user can't access
    // 404 redirect?
    if (error) return <h1>{error}</h1>;

    const siteGroup = siteGroups[syncoId];
    if (!siteGroup) {
      return (
        <p>
          There don't seem to be any hits for this site in the selected period
        </p>
      );
    }
    const ruleGroup = siteGroup.ruleGroups[ruleId];

    if (!ruleGroup) {
      return (
        <p>There don't seem to be any hits for this rule/site combination</p>
      );
    }

    const { ruleName } = ruleGroup[0];
    // TODO: Invalid syncoId/ruleId combination message
    return (
      <>
        <Helmet title={`Insight - ${ruleName}`} />
        <Wrap>
          <Equip
            insightType={routes.InsightType.Monitoring}
            hits={ruleGroup}
            ruleName={ruleName}
            userAccess={userAccess}
            syncoId={syncoId}
            activeSite={syncoId}
            ruleId={ruleId}
            showSnoozed={showSnoozed}
            sortOption={sort.option}
            sortDirection={sort.direction}
            updateSort={this.handleSort}
            showModal={this.props.showModal}
            urlHistory={this.props.urlHistory}
            selectedSites={this.props.selectedSites}
          />
        </Wrap>
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(EquipScene);
